import i18n from "../i18n";

import axios from "axios";
import { describePermissionDenial } from "./permissionError";

/**
 * Backend validation rules are named for the field they guard, not written
 * for the person who hit them: "Editor not exists" is a rule id, and it does
 * not say who is missing or what to do about it. These are the ones a person
 * can actually act on, rewritten as the instruction they imply.
 *
 * A workflow role is checked against the member's rights on every save, so a
 * right withdrawn after the contract was set up fails every later edit — the
 * message has to point at reassigning the role, not at the field.
 */
const RULE_MESSAGES: Record<string, string> = {
  "editor not exists":
    "The Editor named on this contract no longer holds the Editor right, so it cannot be saved. Reassign the Editor under User Roles.",
  "initiator not exists":
    "The Initiator named on this contract no longer holds the Initiator right. Reassign the Initiator under User Roles.",
  "reviewer not exists":
    "A Reviewer named on this contract no longer holds the Reviewer right. Reassign the Reviewers under User Roles.",
  "approver not exists":
    "An Approver named on this contract no longer holds the Approver right. Reassign the Approvers under User Roles.",
};

/** Rewrites the rules we have a plainer sentence for; anything else is passed
 *  through untouched, since an unknown reason is better than a vague one. */
function describeRule(message: string): string {
  const rule = message.trim().toLowerCase();
  const plain = RULE_MESSAGES[rule];
  return plain
    ? i18n.t(`apiRules.${rule.split(" ")[0]}`, {
        ns: "common",
        defaultValue: plain,
      })
    : message;
}

/**
 * Extracts a human-readable message from a failed API call. Backend
 * validation failures come back as { message, errors: string[] } (e.g.
 * FluentValidation's "Command Validation Errors for type X" with an
 * errors array holding the actual per-field reasons) — those specific
 * reasons are far more useful to show than the generic wrapper message.
 */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    // [PermissionAuthorize] denials come back as a bare 403 with no body,
    // which otherwise surfaced as "Request failed with status code 403".
    if (err.response?.status === 403) {
      return describePermissionDenial(err.config?.url);
    }
    const data = err.response?.data;
    if (data && typeof data === "object") {
      // Domain-rule refusals (e.g. "cannot delete: still used by …") come
      // back as { messages: [...] }; validation failures as { errors: [...] }.
      const errors =
        (data as { errors?: unknown; Errors?: unknown }).errors ??
        (data as { Errors?: unknown }).Errors ??
        (data as { messages?: unknown }).messages ??
        (data as { Messages?: unknown }).Messages;
      if (Array.isArray(errors) && errors.length > 0) {
        return errors.map((e) => describeRule(String(e))).join(" ");
      }
      const message =
        (data as { message?: unknown }).message ??
        (data as { Message?: unknown }).Message;
      if (typeof message === "string" && message) {
        return describeRule(message);
      }
    }
    if (typeof data === "string" && data) {
      return data;
    }
  }
  // Thunks that already unwrapped an axios error re-throw a plain Error
  // carrying the extracted message — honor that instead of re-extracting.
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
}
